/**
 * Recursively filters an object, replacing all leaf values with '[FILTERED]'
 * while preserving the structure (keys, nesting, array shapes).
 * The 'errors' key is preserved without filtering as it contains useful debugging information.
 */
export function filterSensitiveData(obj: unknown): unknown {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => {
      if (item === null || item === undefined) {
        return item
      }
      if (typeof item === 'object') {
        return filterSensitiveData(item)
      }
      return '[FILTERED]'
    })
  }

  if (typeof obj === 'object') {
    const filtered: Record<string, unknown> = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const value = (obj as Record<string, unknown>)[key]
        // Preserve 'errors' key without filtering - it's useful for debugging
        if (key === 'errors') {
          filtered[key] = value
        } else if (value === null || value === undefined) {
          filtered[key] = value
        } else if (typeof value === 'object') {
          filtered[key] = filterSensitiveData(value)
        } else {
          filtered[key] = '[FILTERED]'
        }
      }
    }
    return filtered
  }

  return obj
}

type TimingContext = Record<string, unknown>
type ResourceTimingSnapshot = ReadonlySet<string>

export function parseServerTiming(header: string | null): Record<string, number> | undefined {
  if (!header) return undefined

  const timings: Record<string, number> = {}
  for (const entry of splitHeaderValue(header, ',')) {
    const [rawName, ...parameters] = splitHeaderValue(entry, ';')
    const name = rawName?.trim()
    if (!name) continue

    const duration = parameters.map(parseDurationParameter).find(value => value !== undefined)
    if (duration !== undefined) {
      timings[name] = duration
    }
  }

  return Object.keys(timings).length > 0 ? timings : undefined
}

function parseDurationParameter(parameter: string): number | undefined {
  const match = /^\s*dur\s*=\s*(\d+(?:\.\d+)?)\s*$/i.exec(parameter)
  if (!match) return undefined

  const duration = Number(match[1])
  return Number.isFinite(duration) ? duration : undefined
}

export function addBrowserTimingContext(
  json: unknown,
  serverTimingHeader: string | null,
  resourceTiming?: PerformanceResourceTiming,
): void {
  if (!isRecord(json) || !isRecord(json.__trace)) return

  const trace = json.__trace
  const existingTimingContext = isRecord(trace.timing_context) ? trace.timing_context : {}
  const headerServerTiming = parseServerTiming(serverTimingHeader)
  const resourceServerTiming = getResourceServerTiming(resourceTiming)
  const serverTiming =
    headerServerTiming || resourceServerTiming ? {...resourceServerTiming, ...headerServerTiming} : undefined
  const browserTimingContext: TimingContext = {}

  if (serverTiming) {
    browserTimingContext.server_timing_ms = serverTiming
  }

  if (resourceTiming) {
    addDuration(browserTimingContext, 'browser_resource_timing_duration_ms', resourceTiming.duration)
    addPhaseDuration(browserTimingContext, 'browser_queueing_ms', resourceTiming.fetchStart, resourceTiming.startTime)
    addPhaseDuration(
      browserTimingContext,
      'browser_connection_stalled_ms',
      resourceTiming.domainLookupStart,
      resourceTiming.fetchStart,
    )
    addPhaseDuration(
      browserTimingContext,
      'browser_dns_lookup_ms',
      resourceTiming.domainLookupEnd,
      resourceTiming.domainLookupStart,
    )
    addPhaseDuration(
      browserTimingContext,
      'browser_tcp_connection_ms',
      resourceTiming.connectEnd,
      resourceTiming.connectStart,
    )
    if (resourceTiming.secureConnectionStart > 0) {
      addPhaseDuration(
        browserTimingContext,
        'browser_tls_negotiation_ms',
        resourceTiming.connectEnd,
        resourceTiming.secureConnectionStart,
      )
    }
    addPhaseDuration(
      browserTimingContext,
      'browser_waiting_for_server_response_ms',
      resourceTiming.responseStart,
      resourceTiming.requestStart,
    )
    addPhaseDuration(
      browserTimingContext,
      'browser_content_download_ms',
      resourceTiming.responseEnd,
      resourceTiming.responseStart,
    )
  }

  trace.timing_context = {...existingTimingContext, ...browserTimingContext}
}

export function captureResourceTimingSnapshot(): ResourceTimingSnapshot {
  return new Set(performance.getEntriesByType('resource').map(resourceTimingKey))
}

export function getBrowserResourceTiming(
  url: string,
  requestStartedAt: number,
  snapshot: ResourceTimingSnapshot,
): PerformanceResourceTiming | undefined {
  const candidates = performance
    .getEntriesByName(url, 'resource')
    .filter(
      (candidate): candidate is PerformanceResourceTiming =>
        candidate.entryType === 'resource' &&
        !snapshot.has(resourceTimingKey(candidate)) &&
        candidate.startTime >= requestStartedAt &&
        Number.isFinite(candidate.duration) &&
        candidate.duration >= 0,
    )

  return candidates.length === 1 ? candidates[0] : undefined
}

function resourceTimingKey(entry: PerformanceEntry): string {
  return `${entry.name}:${entry.startTime}`
}

function getResourceServerTiming(resourceTiming: PerformanceResourceTiming | undefined) {
  const serverTiming = resourceTiming?.serverTiming
  if (!serverTiming || serverTiming.length === 0) return undefined

  const timings: Record<string, number> = {}
  for (const {name, duration} of serverTiming) {
    if (name && Number.isFinite(duration) && duration > 0) {
      timings[name] = duration
    }
  }
  return Object.keys(timings).length > 0 ? timings : undefined
}

function addPhaseDuration(timingContext: TimingContext, key: string, endTime: number, startTime: number): void {
  if (startTime <= 0 || endTime <= 0) return
  addDuration(timingContext, key, endTime - startTime)
}

function addDuration(timingContext: TimingContext, key: string, duration: number): void {
  if (Number.isFinite(duration) && duration >= 0) {
    timingContext[key] = roundDuration(duration)
  }
}

function splitHeaderValue(value: string, separator: ',' | ';'): string[] {
  const parts: string[] = []
  let partStart = 0
  let quoted = false
  let escaped = false

  for (let index = 0; index < value.length; index++) {
    const character = value[index]
    if (escaped) {
      escaped = false
    } else if (character === '\\' && quoted) {
      escaped = true
    } else if (character === '"') {
      quoted = !quoted
    } else if (character === separator && !quoted) {
      parts.push(value.slice(partStart, index))
      partStart = index + 1
    }
  }

  parts.push(value.slice(partStart))
  return parts
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function roundDuration(duration: number): number {
  return Math.round(duration * 100) / 100
}
